import React, { useState, useEffect, useMemo, useRef } from 'react';
import { message, Modal } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { Get_Account_Experts, Get_Expert_Profile, Update_Expert } from '../../../../../services/userAPI';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import './stars.css';
import DeleteIcon from "../../../../../Images/starsDeleteIcon.png";
import { AgGridReact } from 'ag-grid-react';
import CustomNoRowsOverlay from '../../../../CustomNoRowsOverlay';
import { SearchOutlined } from "@ant-design/icons";

const Stars = () => {
    const [localData, setLocalData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const location = useLocation();
    const accountId = location.state?.accountId;
    const name = location.state?.name;
    const accountName = location.state?.accountName;

    // ag-grid state
    const gridApiRef = useRef(null);
    const [displayedRowCount, setDisplayedRowCount] = useState(null);

    // Delete confirmation modal state
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Fixed constants
    const limit = 1000;
    const sortByField = 'created_at';
    const sortOrder = 'desc';

    const [updateExpertStatus] = useMutation(Update_Expert);
    const [fetchExpertProfile] = useLazyQuery(Get_Expert_Profile);

    // Fetch experts
    const { data, loading, error, refetch } = useQuery(Get_Account_Experts, {
        variables: {
            pagingCriteria: {
                limit,
                cursor: null,
                sortByField,
                sortOrder
            },
            accountId: accountId,
        },
        skip: !accountId,
        fetchPolicy: 'cache-and-network'
    });

    // Extract list and meta - use useMemo to prevent recomputation
    const experts = useMemo(() => data?.getAccountExperts?.data || [], [data]);
    const pageMeta = useMemo(() => data?.getAccountExperts?.pageMeta || {}, [data]);

    // Process data when experts change
    useEffect(() => {
        if (experts && experts.length > 0) {
            const formattedData = experts.map((expert) => {
                // Calculate formatted duration for display if needed
                let formattedDuration = "00:00";
                if (expert.introVideoLengthInSeconds) {
                    const minutes = Math.floor(expert.introVideoLengthInSeconds / 60);
                    const seconds = expert.introVideoLengthInSeconds % 60;
                    formattedDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                }

                // Format dateOfJoining to DD-MM-YYYY
                let formattedDoj = 'N/A';
                if (expert.dateOfJoining) {
                    try {
                        const date = new Date(expert.dateOfJoining);
                        if (!isNaN(date.getTime())) {
                            const day = date.getDate().toString().padStart(2, '0');
                            const month = (date.getMonth() + 1).toString().padStart(2, '0');
                            const year = date.getFullYear();
                            formattedDoj = `${day}-${month}-${year}`;
                        }
                    } catch (e) {
                        console.error('Invalid date format:', expert.dateOfJoining);
                    }
                }

                return {
                    key: expert.id,
                    empId: expert.employeeId || '',
                    name: expert.name || '',
                    gender: expert.gender || 'N/A',
                    doj: formattedDoj,
                    designation: expert.designation || '',
                    city: expert.location || 'N/A',
                    branch: expert.branch || 'N/A',
                    manager: expert.manager || 'N/A',
                    // Add all fields needed for editing
                    profileSummary: expert.profileSummary || '',
                    introVideoCode: expert.introVideoCode || '',
                    introVideoThumb: expert.introVideoThumb || '',
                    introVideoTitle: expert.introVideoTitle || '',
                    introVideoDescription: expert.introVideoDescription || '',
                    introVideoLanguageId: expert.introVideoLanguageId || 1,
                    introVideoLengthInSeconds: expert.introVideoLengthInSeconds || 0,
                    formattedDuration: formattedDuration
                };
            });
            setLocalData(formattedData);
        } else {
            setLocalData([]);
        }
    }, [experts]);

    // CSV Export using ag-grid filter model
    const handleExport = async () => {
        try {
            const api = gridApiRef.current;
            let displayedRows = [];

            const getDisplayValue = (row, field) => {
                const val = row[field];
                return val === null || val === undefined ? '' : String(val);
            };

            if (api) {
                const filterModel = api.getFilterModel();
                if (filterModel && Object.keys(filterModel).length > 0) {
                    displayedRows = [...localData];
                    Object.keys(filterModel).forEach(field => {
                        const filter = filterModel[field];
                        if (filter && filter.filter) {
                            const filterValue = filter.filter.toLowerCase();
                            displayedRows = displayedRows.filter(row => {
                                const cellValue = getDisplayValue(row, field);
                                return cellValue.toLowerCase().includes(filterValue);
                            });
                        }
                    });
                } else {
                    displayedRows = [...localData];
                }
            } else {
                displayedRows = [...localData];
            }

            if (displayedRows.length === 0) {
                message.warning('No data available to export.');
                return;
            }

            const exportColumns = [
                { field: 'empId', header: 'Employee ID' },
                { field: 'name', header: 'Name' },
                { field: 'gender', header: 'Gender' },
                { field: 'doj', header: 'Date of Joining' },
                { field: 'designation', header: 'Designation' },
                { field: 'city', header: 'City' },
                { field: 'branch', header: 'Branch' },
                { field: 'manager', header: 'Manager Name' },
            ];

            const csvHeader = exportColumns.map(col => col.header).join(',');
            const csvRows = displayedRows.map(row => {
                return exportColumns.map(col => {
                    let value = row[col.field] || '';
                    if (typeof value === 'string') {
                        value = value.replace(/"/g, '""');
                        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                            value = `"${value}"`;
                        }
                    }
                    return value;
                }).join(',');
            });

            const csvContent = [csvHeader, ...csvRows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `stars_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export completed successfully! (${displayedRows.length} records exported)`);
        } catch (err) {
            console.error('Error exporting data:', err);
            message.error('An error occurred while exporting data.');
        }
    };

    const handleExportClick = () => {
        handleExport();
    };

    const handleEditClick = async (record) => {
        try {
            const { data } = await fetchExpertProfile({
                variables: { userId: record.key },
                fetchPolicy: 'network-only',
            });

            const expert = data?.getExpertProfile;
            if (!expert) {
                message.error('Failed to load expert profile');
                return;
            }

            const formattedDuration = `${Math.floor(expert.introVideoLengthInTotalSeconds / 60)
                .toString()
                .padStart(2, '0')}:${(expert.introVideoLengthInTotalSeconds % 60)
                    .toString()
                    .padStart(2, '0')}`;

            const editPayload = {
                isEdit: true,
                accountId: expert.accountId,
                name: name,
                accountName: accountName,
                editData: {
                    stars: [
                        {
                            name: `${expert.name}`,
                            userId: expert.id,
                            accountId: expert.accountId,
                            starDescription: expert.profileSummary || "",
                            youtubeCode: expert.introVideoCode || "",
                            videoThumbnailCode: expert.introVideoThumbnail || "",
                            videoTitle: expert.introVideoTitle || "",
                            videoDescription: expert.introVideoDescription || "",
                            videoLanguageId: expert.introVideoLanguageId,
                            videoLanguage: expert.introVideoLanguageId,
                            duration: formattedDuration,
                            email: expert.email,
                            phoneNumber: expert.phoneNumber,
                            designation: expert.designation,
                            location: expert.location,
                            preferredLanguages: expert.preferredLanguages || [],
                            introVideoLengthInTotalSeconds: expert.introVideoLengthInSeconds || 0,
                            formattedDuration
                        },
                    ],
                },
            };
            navigate('/add-stars', { state: editPayload });
        } catch (error) {
            console.error("Failed to fetch expert profile:", error);
            message.error('Failed to load expert data');
        }
    };

    // Show delete confirmation modal
    const showDeleteConfirm = (record) => {
        setRecordToDelete(record);
        setIsDeleteModalVisible(true);
    };

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        if (!recordToDelete) return;

        setIsDeleting(true);
        try {
            const response = await updateExpertStatus({
                variables: {
                    userId: parseInt(recordToDelete.key),
                    profileSummary: recordToDelete.profileSummary,
                    introVideoCode: recordToDelete.introVideoCode,
                    introVideoThumb: recordToDelete.introVideoThumb,
                    phoneNumber: recordToDelete.phoneNumber || "",
                    introVideoTitle: recordToDelete.introVideoTitle,
                    introVideoDescription: recordToDelete.introVideoDescription,
                    introVideoLanguageId: recordToDelete.introVideoLanguageId,
                    introVideoLengthInSeconds: recordToDelete.introVideoLengthInSeconds,
                    status: 0, // soft-delete
                }
            });

            if (response?.data?.updateExpert?.success) {
                message.success('Expert deleted successfully');
                refetch(); // refresh the list
            } else {
                message.error('Failed to delete expert');
            }
        } catch (error) {
            console.error("Delete failed:", error);
            message.error('An error occurred while deleting');
        } finally {
            setIsDeleting(false);
            setIsDeleteModalVisible(false);
            setRecordToDelete(null);
        }
    };

    // Handle delete cancel
    const handleDeleteCancel = () => {
        setIsDeleteModalVisible(false);
        setRecordToDelete(null);
    };

    // ag-grid column definitions
    const columnDefs = useMemo(() => [
        { headerName: 'Emp. ID', headerTooltip: 'Emp. ID', field: 'empId', sortable: true, filter: 'agTextColumnFilter', floatingFilter: true },
        { headerName: 'Name', headerTooltip: 'Name', field: 'name', sortable: true, filter: 'agTextColumnFilter', floatingFilter: true },
        { headerName: 'Gender', headerTooltip: 'Gender', field: 'gender', sortable: true, filter: 'agTextColumnFilter', floatingFilter: true },
        { headerName: 'Date of Joining', headerTooltip: 'Date of Joining', field: 'doj', sortable: true, filter: 'agTextColumnFilter', floatingFilter: true },
        { headerName: 'Designation', headerTooltip: 'Designation', field: 'designation', sortable: true, filter: 'agTextColumnFilter', floatingFilter: true },
        { headerName: 'City', headerTooltip: 'City', field: 'city', sortable: true, filter: 'agTextColumnFilter', floatingFilter: true },
        { headerName: 'Branch', headerTooltip: 'Branch', field: 'branch', sortable: true, filter: 'agTextColumnFilter', floatingFilter: true },
        { headerName: 'Manager Name', headerTooltip: 'Manager Name', field: 'manager', sortable: true, filter: 'agTextColumnFilter', floatingFilter: true },
        {
            headerName: 'Action',
            headerTooltip: 'Action',
            field: 'action',
            filter: false,
            sortable: false,
            cellRenderer: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: '100%' }}>
                    <button
                        className="addStarseditandimage"
                        onClick={() => handleEditClick(params.data)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                        <span className="rolemanagementeditname" style={{ margin: 0 }}>Edit</span>
                    </button>
                    <span aria-hidden="true" style={{ width: 1, height: 18, backgroundColor: '#828282', flexShrink: 0 }} />
                    <img
                        style={{ cursor: 'pointer', display: 'block', width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
                        src={DeleteIcon}
                        alt="Delete"
                        onClick={() => showDeleteConfirm(params.data)}
                    />
                </div>
            ),
            cellStyle: { padding: '0 10px', display: 'flex', alignItems: 'center' },
        },
    ], []);

    const defaultColDef = useMemo(() => ({
        flex: 1,
        minWidth: 100,
        resizable: true,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        cellStyle: { padding: '10px' },
        tooltipValueGetter: (params) => params.value,
    }), []);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const onFilterChanged = (params) => {
        if (params?.api) {
            const isFilterActive = params.api.isAnyFilterPresent();
            if (isFilterActive) {
                const filterModel = params.api.getFilterModel();
                let filteredData = [...localData];
                Object.keys(filterModel).forEach(field => {
                    const filter = filterModel[field];
                    if (filter && filter.filter) {
                        const filterValue = filter.filter.toLowerCase();
                        filteredData = filteredData.filter(row => {
                            const val = row[field];
                            const cellValue = val === null || val === undefined ? '' : String(val);
                            return cellValue.toLowerCase().includes(filterValue);
                        });
                    }
                });
                setDisplayedRowCount(filteredData.length);
                if (filteredData.length === 0) {
                    params.api.showNoRowsOverlay();
                } else {
                    params.api.hideOverlay();
                }
            } else {
                setDisplayedRowCount(null);
                params.api.hideOverlay();
            }
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
            </div>
        );
    }

    const filteredData = localData.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.empId?.toString().includes(searchTerm) ||
        item.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.city?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Render component
    return (
        <div style={{ backgroundColor: "white", borderRadius: "1%", padding: "20px", height: "100%" }}>
            <div style={{ marginBottom: '16px' }}>
                <h2 style={{ fontSize: '20px', marginBottom: '11px' }}>
                    Stars
                </h2>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            position: 'relative',
                        }}
                    >
                        <input
                            type="text"
                            placeholder="Search Users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                height: '43px',
                                border: '1px solid #d9d9d9',
                                borderRadius: '8px',
                                padding: '0 45px 0 16px',
                                fontSize: '14px',
                                outline: 'none',
                            }}
                        />
                        <SearchOutlined
                            style={{
                                position: 'absolute',
                                right: '16px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '14px',
                                color: '#000',
                                cursor: 'pointer',
                            }}
                        />
                    </div>

                    <button
                        className="addStars"
                        onClick={() =>
                            navigate('/add-stars', {
                                state: {
                                    accountId,
                                    accountName,
                                    name,
                                    isEdit: false,
                                },
                            })
                        }
                    >
                        Add Star
                    </button>
                </div>
            </div>

            {error && <div>Error loading data: {error.message}</div>}

            <div className="ag-grid-wrapper">
                <AgGridReact
                    rowData={filteredData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    paginationPageSize={20}
                    onGridReady={onGridReady}
                    onFilterChanged={onFilterChanged}
                    className="user-management-ag-grid"
                    suppressCellFocus={true}
                    domLayout="autoHeight"
                    animateRows={true}
                    suppressHorizontalScroll={true}
                    noRowsOverlayComponent={() => <CustomNoRowsOverlay message="No users found" />}
                    tooltipShowDelay={0}
                />
            </div>

            <div className="footer" style={{ textAlign: 'right', padding: '10px', fontFamily: 'Inter' }}>
                Total stars:{' '}
                <span style={{ fontWeight: 700, fontSize: '13px' }}>
                    {displayedRowCount !== null && displayedRowCount !== localData.length
                        ? `${displayedRowCount} of ${localData.length}`
                        : localData.length}
                </span>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                wrapClassName="stars-delete-modal"
                open={isDeleteModalVisible}
                closable={false}
                footer={[
                    <div key="footer-buttons" style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '12px',
                        width: '100%',
                    }}>
                        <button
                            className='cancelButtonStar'
                            onClick={handleDeleteCancel}>
                            Cancel
                        </button>
                        <button
                            className='deleteButtonStar'
                            onClick={handleDeleteConfirm}
                            disabled={isDeleting}
                            style={{ opacity: isDeleting ? 0.7 : 1, cursor: isDeleting ? 'not-allowed' : 'pointer' }}
                        >
                            {isDeleting ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Delete'}
                        </button>
                    </div>
                ]}
                centered
                width={380}
                styles={{ footer: { marginTop: 8 } }}
            // onCancel={handleDeleteCancel}
            >
                <p style={{ fontFamily: "Inter", fontWeight: "500", fontSize: "16px", textAlign: "center" }}>
                    Are you sure to delete {recordToDelete?.name} from Stars?
                </p>
            </Modal>
        </div>
    );
};

export default Stars;
